// Pure helpers for the case dashboard (src/v2/views/components/CaseDashboard).
// The case contract: sitcase/sports-impact-code/docs/case-dashboard.md.

export const JSON_PREFIX = "@@JSON ";

// The case's modes (sitcase app/app_mode.py). Cases before v0.7.0 report the
// old names (normal, sailgp); map them so the dashboard shows one set. A mode
// it doesn't know (an old case's removed f50) keeps its raw name.
const LEGACY_MODES = { normal: "receiver", sailgp: "pairing-only" };
const MODE_KEYS = {
  receiver: "receiver",
  "pairing-only": "pairingOnly",
  offload: "offload",
};

/** A case mode under its current name. */
export const caseMode = (mode) => LEGACY_MODES[mode] || mode;

/** The i18n key under caseDashboard.modes for a mode, or null if unknown. */
export const caseModeKey = (mode) => MODE_KEYS[caseMode(mode)] || null;

/** The last "@@JSON {...}" result in a command's output lines, or null. */
export function extractJson(lines) {
  if (!Array.isArray(lines)) return null;
  for (let i = lines.length - 1; i >= 0; i--) {
    const line = lines[i];
    if (typeof line === "string" && line.startsWith(JSON_PREFIX)) {
      try {
        return JSON.parse(line.slice(JSON_PREFIX.length));
      } catch {
        return null;
      }
    }
  }
  return null;
}

/**
 * The case stores UTC without a zone ("2026-10-14 11:35:13.123"). Returns a
 * Date, or null for empty/unparseable input. Values that already carry a zone
 * are left alone.
 */
export function parseUtc(value) {
  if (!value) return null;
  if (value instanceof Date) return value;
  let s = String(value).trim().replace(" ", "T");
  if (!/[zZ]|[+-]\d\d:?\d\d$/.test(s)) s += "Z";
  const d = new Date(s);
  return Number.isNaN(d.getTime()) ? null : d;
}

// A session older than this with no end is abandoned (the case expires it).
export const SESSION_MAX_AGE_MS = 6 * 60 * 60 * 1000;

/** True when `session` counts as the case's current session at `now`. */
export function isCurrentSession(session, now = Date.now()) {
  if (!session || session.ended_at) return false;
  const started = parseUtc(session.started_at);
  if (!started) return false;
  return now - started.getTime() < SESSION_MAX_AGE_MS;
}

// "Last heard" thresholds for the live roster.
export const HEARD_OK_MS = 60 * 1000;
export const HEARD_WARN_MS = 5 * 60 * 1000;

/** "ok" (heard in the last minute), "warn" (last 5 min), "bad", or "never". */
export function lastHeardTone(lastContact, now = Date.now()) {
  const d = parseUtc(lastContact);
  if (!d) return "never";
  const age = now - d.getTime();
  if (age <= HEARD_OK_MS) return "ok";
  if (age <= HEARD_WARN_MS) return "warn";
  return "bad";
}

// `docker-compose logs -t` line followed by the app's columns
// (app/log_setup.py): level (blank for INFO), source tag, message.
//   "sit-app-1  | 2026-10-14T12:32:25.755123456Z       pairing  [timing] …"
const DOCKER_LINE =
  /^(?<svc>\S+)\s+\|\s(?<ts>\d{4}-\d\d-\d\dT\d\d:\d\d:\d\d(?:\.\d+)?Z?)\s?(?<rest>.*)$/;
// INFO lines have a blank level column: exactly 5 spaces + the separator.
// Requiring that keeps indented traceback lines from parsing as a tag.
const APP_COLUMNS =
  /^(?:(?<level>DBG|WARN|ERROR|CRIT)\s+|\s{6})(?<tag>\S+)\s+(?<msg>.*)$/;
const LEVELS = { DBG: "debug", WARN: "warn", ERROR: "error", CRIT: "error" };

/**
 * One log line -> { time: Date|null, service, level, tag, message, raw }.
 * level is debug | info | warn | error. Lines that don't match the app format
 * (tracebacks, docker-compose output) come back as info with tag null.
 */
export function parseLogLine(raw) {
  const line = String(raw ?? "").replace(/\r$/, "");
  const m = line.match(DOCKER_LINE);
  if (!m) {
    return {
      time: null,
      service: null,
      level: "info",
      tag: null,
      message: line,
      raw: line,
    };
  }
  const { svc, ts, rest } = m.groups;
  const time = parseUtc(ts.endsWith("Z") ? ts : `${ts}Z`);
  // The app pads the level column to 5 characters ("WARN ", "     ").
  const cols = rest.match(APP_COLUMNS);
  if (cols) {
    const lvl = cols.groups.level || "";
    return {
      time,
      service: svc,
      level: LEVELS[lvl] || "info",
      tag: cols.groups.tag,
      message: cols.groups.msg,
      raw: line,
    };
  }
  return {
    time,
    service: svc,
    level: "info",
    tag: null,
    message: rest,
    raw: line,
  };
}

/**
 * Overall status for the big header: "running", "attention", "stopped",
 * "unknown". `status` is the `status` command JSON, `health` the `health` one.
 */
export function overallStatus(status, health) {
  if (!status) return "unknown";
  if (status.summary === "stopped" || status.summary === "not running")
    return "stopped";
  if (status.summary === "partial") return "attention";
  if (health && (health.status === "warn" || health.status === "critical"))
    return "attention";
  if (status.summary === "running") return "running";
  return "unknown";
}

// Issue codes the case reports (docs/case-health.md). Unknown codes fall back
// to the raw code in the UI.
export const HEALTH_ISSUES = [
  "cpu_hot",
  "undervoltage",
  "undervoltage_earlier",
  "throttled",
  "throttled_earlier",
  "memory_low",
  "disk_full",
  "cpu_busy",
  "adapter_missing",
  "adapter_down",
  "db_down",
  "scanner_quiet",
];

/** case_metrics.issues is a comma-separated string; health.issues an array. */
export function issueList(issues) {
  if (Array.isArray(issues)) return issues.filter(Boolean);
  if (!issues) return [];
  return String(issues)
    .split(",")
    .map((s) => s.trim())
    .filter(Boolean);
}

/** Seconds -> "45s", "12m", "3h 5m", "2d 4h". */
export function formatDuration(seconds) {
  if (seconds == null || Number.isNaN(Number(seconds))) return "—";
  const s = Math.max(0, Math.round(Number(seconds)));
  if (s < 60) return `${s}s`;
  if (s < 3600) return `${Math.floor(s / 60)}m`;
  if (s < 86400)
    return `${Math.floor(s / 3600)}h ${Math.floor((s % 3600) / 60)}m`;
  return `${Math.floor(s / 86400)}d ${Math.floor((s % 86400) / 3600)}h`;
}

/** Merge newly polled impacts into a list, dropping ids already present. */
export function mergeById(existing, incoming) {
  if (!incoming?.length) return existing;
  const seen = new Set(existing.map((r) => r.id));
  const add = incoming.filter((r) => !seen.has(r.id));
  return add.length ? [...existing, ...add] : existing;
}

/** Stable colour per player id/name for charts. */
const PLAYER_COLORS = [
  "#E07A3A",
  "#3B82F6",
  "#10B981",
  "#8B5CF6",
  "#F59E0B",
  "#EC4899",
  "#14B8A6",
  "#6366F1",
  "#84CC16",
  "#EF4444",
];
export function playerColor(key) {
  const s = String(key ?? "");
  let h = 0;
  for (let i = 0; i < s.length; i++) h = (h * 31 + s.charCodeAt(i)) >>> 0;
  return PLAYER_COLORS[h % PLAYER_COLORS.length];
}

/**
 * The case software version from `status` (manage.py dashboard status):
 * { display, base, product, channel, commit, pendingRestart } or null.
 * Formats match the wearable firmware ("v1.0.0 (v0.4.12)", "v0.4.12-dev+abc").
 * `pendingRestart` is the installed internal version when it differs from
 * the running one (updated on disk, not restarted yet).
 */
export function caseVersion(status) {
  const v = status?.version;
  if (!v) return null;
  const running = v.running || null;
  const installed = v.installed || null;
  if (!running && !installed) return null;
  const base = running?.base_version || installed;
  return {
    display: running?.version || installed,
    base,
    product: running?.product_version || null,
    channel: running?.channel || null,
    commit: running?.commit || status?.git?.commit || null,
    pendingRestart:
      running && installed && installed !== running.base_version
        ? installed
        : null,
  };
}

/**
 * How you reach the case, from the `network` command:
 * { ip, via: "wifi" | "field" | "ethernet" | "none", ssid, signal }.
 * The Wi-Fi address comes first: that's the one the dashboard uses.
 */
export function networkSummary(net) {
  if (!net) return null;
  const ifaces = Array.isArray(net.interfaces) ? net.interfaces : [];
  const wlan = ifaces.find((i) => i.name?.startsWith("wlan"));
  const routable = ifaces.find((i) => i.ip && !i.ip.startsWith("169.254."));
  const ip = wlan?.ip || net.ip || routable?.ip || ifaces[0]?.ip || null;
  if (net.mode === "ap")
    return {
      ip,
      via: "field",
      ssid: net.ap_ssid || net.ssid || null,
      signal: null,
    };
  if (net.wifi?.ssid || (net.mode === "client" && net.ssid))
    return {
      ip,
      via: "wifi",
      ssid: net.wifi?.ssid || net.ssid,
      signal: net.wifi?.signal ?? null,
    };
  if (routable)
    return { ip: routable.ip, via: "ethernet", ssid: null, signal: null };
  return { ip, via: "none", ssid: null, signal: null };
}

// Bluetooth range (case software v0.9.0+): the PHY the case scans and pairs
// on. "coded" is long range; "1m" is for receivers that can't hear Coded.
export const BLE_RANGES = ["coded", "1m"];

/**
 * The case's Bluetooth range from `status` and `health`:
 * { setting, listening } or null when the case is too old to have one.
 * `listening` (from health) is what the scanner runs right now; null while it
 * isn't scanning. It differs from `setting` briefly after a switch.
 */
export function caseBleRange(status, health) {
  const setting = status?.ble_range || health?.ble_range?.setting || null;
  if (!BLE_RANGES.includes(setting)) return null;
  const hr = health?.ble_range;
  return {
    setting: hr?.setting || setting,
    listening: hr && "listening" in hr ? hr.listening : setting,
  };
}

/** True for an older case refusing a command it doesn't have. */
export const isRefusedCommand = (error) =>
  /not a dashboard command|Not an allowed command/i.test(
    String(error?.message || error || "")
  );

// Session ids the case accepts for `session-delete`.
export const SESSION_ID_RE = /^[A-Za-z0-9_.:-]{1,64}$/;

/** Bytes -> "512 B", "3.4 KB", "12 MB", "1.2 GB" (1024-based); "—" if null. */
export function formatBytes(bytes) {
  if (bytes == null || Number.isNaN(Number(bytes))) return "—";
  const n = Math.max(0, Number(bytes));
  if (n < 1024) return `${Math.round(n)} B`;
  const units = ["KB", "MB", "GB", "TB"];
  let v = n / 1024;
  let i = 0;
  while (v >= 1024 && i < units.length - 1) {
    v /= 1024;
    i += 1;
  }
  return `${v < 10 ? v.toFixed(1) : Math.round(v)} ${units[i]}`;
}
